import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import NodeDrawerAccordion from "@features/builder/NodeDrawer/NodeDrawerAccordion"
import { FormHelperText, Grid } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"
import { AppNode, DeterministicNode, EdgeData, LagNode } from "@features/builder/graph/types"
import { Edge, useReactFlow } from "@xyflow/react"
import CodeMirror, { EditorView, ViewUpdate } from "@uiw/react-codemirror"
import { expressionLanguage } from "@features/builder/NodeDrawer/NodeForm/NodeExpressionAccordion/expressionLanguage"
import { diagnosticCount } from "@codemirror/lint"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { useCallback } from "react"
import { getErrorMessage } from "@utils/form"

const NodeExpressionAccordion = (props: { node?: LagNode | DeterministicNode }) => {
  const form = useFormContext<NodeFormValues>()
  const { getNodes } = useReactFlow<AppNode, Edge<EdgeData>>()
  const nodes = getNodes()
  const { getNodeValidation, setNodeValidation } = useNodesValidation()
  const errorMessage = getErrorMessage(form.formState.errors, "expression")
  const lagExpressionErrors = getNodeValidation(props.node?.id, "EXPRESSION")?.errors ?? []
  const linterExpressionErrors =
    getNodeValidation(props.node?.id, "EXPRESSION_LINTING")?.errors ?? []
  const hasLagExpressionErrors = lagExpressionErrors.length > 0

  const experssionErrors = [
    errorMessage,
    ...[...lagExpressionErrors, ...linterExpressionErrors].map(({ msg }) => msg),
  ].filter(Boolean)

  const handleUpdate = useCallback(
    (viewUpdate: ViewUpdate) => {
      if (!props.node) return

      const isValid = diagnosticCount(viewUpdate.state) === 0
      if (isValid) {
        setNodeValidation(props.node.id, "EXPRESSION_LINTING", {
          status: "valid",
          errors: [],
        })
      } else {
        setNodeValidation(props.node.id, "EXPRESSION_LINTING", {
          status: "error",
          errors: [{ msg: "Invalid expression" }],
        })
      }
    },
    [props.node, setNodeValidation],
  )

  const extensions = [
    ...[props.node ? expressionLanguage(props.node, nodes) : []],
    EditorView.lineWrapping,
  ]

  return (
    <NodeDrawerAccordion id="node-expression" label="Expression">
      <Grid item>
        <Controller
          name="expression"
          control={form.control}
          render={({ field }) => (
            <div>
              <div
                style={
                  errorMessage || hasLagExpressionErrors
                    ? { borderColor: "red", borderStyle: "solid" }
                    : undefined
                }
              >
                <CodeMirror
                  {...field}
                  onUpdate={handleUpdate}
                  editable={!field.disabled}
                  extensions={extensions}
                  basicSetup={{
                    allowMultipleSelections: true,
                    autocompletion: true,
                    bracketMatching: true,
                    closeBrackets: true,
                    foldGutter: false,
                    highlightActiveLine: false,
                    lineNumbers: false,
                    tabSize: 0,
                  }}
                />
              </div>
              {experssionErrors.map((errMsg, index) => (
                <FormHelperText key={`${errMsg}-${index}`} error>
                  - {errMsg}
                </FormHelperText>
              ))}
            </div>
          )}
        />
      </Grid>
    </NodeDrawerAccordion>
  )
}

export default NodeExpressionAccordion
